import React, { useState, useEffect, useCallback } from 'react';
import { carouselApiService, FeedCube, SlideRef } from '../services/carouselApiService';
import { slideApiService } from '../services/slideApiService';
import { Slide } from '../types';
import { useShop } from '../contexts/ShopContext';
import FeedCubeCard from './FeedCubeCard';
import CubeDetailModal from './CubeDetailModal';
import SlideCard from './SlideCard';

const PAGE_SIZE = 10;
/** Refs hydrated per feed tile; the tile shows the first 3 that are still visible. Well under hydrate's 200 cap. */
const PREVIEW_HYDRATE_COUNT = 12;

const HomeTab: React.FC = () => {
  const { currentShop, userShops, isInternalMember } = useShop();

  const [cubes, setCubes] = useState<FeedCube[]>([]);
  const [hydratedSlides, setHydratedSlides] = useState<Record<string, Slide[]>>({});
  const [loading, setLoading] = useState(true);
  const [selectedCube, setSelectedCube] = useState<FeedCube | null>(null);
  const [shopHasCubes, setShopHasCubes] = useState<boolean | null>(null);
  const [hasDismissedCube, setHasDismissedCube] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [totalElements, setTotalElements] = useState(0);
  const [shopSlides, setShopSlides] = useState<Slide[]>([]);

  const parseSlideRefs = (refsJson: string): SlideRef[] => {
    try {
      return JSON.parse(refsJson) || [];
    } catch {
      return [];
    }
  };

  const hydrateSlides = useCallback((cubesToHydrate: FeedCube[]) => {
    // Hydrate each cube in parallel, updating state progressively as each resolves
    for (const cube of cubesToHydrate) {
      // Hydrate more refs than the 3 the tile shows and keep the first 3 that come back: a cube's first
      // slides may since have been sold, unpublished or deleted, and a tile whose first 3 all went bad
      // used to vanish even though the rest of the cube was fine (proposal A1).
      const refs = parseSlideRefs(cube.slideRefs).slice(0, PREVIEW_HYDRATE_COUNT);
      if (refs.length === 0) continue;

      // Hydrate this cube's preview refs (mixed shops) in a single carousel-svc call
      carouselApiService.hydrateSlides(refs)
        .then(slides => {
          const slideMap = new Map(slides.map(s => [s.id, s]));
          const ordered = (refs.map(r => slideMap.get(r.slideId)).filter(Boolean) as Slide[]).slice(0, 3);
          setHydratedSlides(prev => ({ ...prev, [cube.cubeId]: ordered }));
        })
        .catch(err => {
          console.error(`HomeTab: Failed to hydrate slides for cube ${cube.cubeId}:`, err);
        });
    }
  }, []);

  const loadFeed = useCallback(async (pageNum: number = 0, append: boolean = false) => {
    setLoading(true);
    try {
      const response = await carouselApiService.getFeedCubes(pageNum, PAGE_SIZE);

      if (append) {
        setCubes(prev => [...prev, ...response.cubes]);
      } else {
        setCubes(response.cubes);
      }

      setHasMore(response.pagination.hasNext);
      setTotalElements(response.pagination.totalElements);
      setPage(pageNum);

      // Fire-and-forget — cards render immediately, images fill in progressively
      hydrateSlides(response.cubes);
    } catch (err) {
      console.error('HomeTab: Failed to load feed:', err);
    } finally {
      setLoading(false);
    }
  }, [hydrateSlides]);

  useEffect(() => {
    setCubes([]);
    setHydratedSlides({});
    setPage(0);
    setShopHasCubes(null);
    setShopSlides([]);
    loadFeed(0);

    // Internal-only: the "shop has cubes" check and the fallback flat-slide grid both hit endpoints
    // that deny external roles (feed-cubes admin + slide-svc slide-data — H4/4b). External
    // (CLIENT/VENDOR) members get only their shared cubes; a no-cubes empty state covers them below.
    if (currentShop?.id && isInternalMember) {
      // Check if shop has any feed cubes (for skeleton card logic)
      carouselApiService.shopHasFeedCubes(currentShop.id)
        .then(result => setShopHasCubes(result))
        .catch(() => setShopHasCubes(null));

      // Load recent slides for fallback grid
      slideApiService.getSlidesForShop(currentShop.id, 0, 20)
        .then(result => setShopSlides(result.items))
        .catch(() => setShopSlides([]));
    }
  }, [currentShop?.id, isInternalMember]);

  const handleLoadMore = () => {
    if (hasMore && !loading) {
      loadFeed(page + 1, true);
    }
  };

  const handleDismiss = async (cubeId: string) => {
    setHasDismissedCube(true);
    setCubes(prev => prev.filter(c => c.cubeId !== cubeId));
    setHydratedSlides(prev => {
      const updated = { ...prev };
      delete updated[cubeId];
      return updated;
    });
    setTotalElements(prev => Math.max(0, prev - 1));

    try {
      await carouselApiService.dismissCube(cubeId);
    } catch (err) {
      console.error('HomeTab: Failed to persist cube dismissal:', err);
    }
  };

  const getShopName = (shopIds: string[]): string | undefined => {
    if (shopIds.length === 1) {
      const shop = userShops?.find(s => s.id === shopIds[0]);
      return shop?.name;
    }
    if (shopIds.length > 1) {
      return `From ${shopIds.length} shops`;
    }
    return undefined;
  };

  // Show messaging card + fallback slide grid when no cubes
  const showMessageCard = !loading && cubes.length === 0 && !hasDismissedCube && shopHasCubes !== null;

  return (
    <div>
      {showMessageCard && (
        <div style={{
          maxWidth: '400px',
          margin: '24px auto',
          padding: '24px',
          textAlign: 'center',
          backgroundColor: 'var(--bg-card)',
          border: '1px solid var(--border-light)',
          borderRadius: '12px',
        }}>
          <h3 style={{ margin: '0 0 8px', color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.9375rem' }}>
            {shopHasCubes ? 'AI Collections Coming Soon' : 'AI Collections'}
          </h3>
          <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
            {shopHasCubes
              ? 'Your AI-curated collections are being generated and will appear here shortly.'
              : 'Keep adding slides for your items. AI will identify themes across your slides and present them in your Home feed.'}
          </p>
        </div>
      )}
      {/* Cube grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))',
        gap: '24px',
      }}>
        {cubes.map(cube => {
          const slides = hydratedSlides[cube.cubeId] || [];
          if (slides.length === 0 && !loading) return null;

          return (
            <FeedCubeCard
              key={cube.cubeId}
              cube={cube}
              slides={slides}
              shopName={getShopName(cube.shopIds)}
              onDismiss={handleDismiss}
              onClick={() => setSelectedCube(cube)}
            />
          );
        })}
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)' }}>
          Loading cubes...
        </div>
      )}

      {/* Load more */}
      {hasMore && !loading && (
        <div style={{ textAlign: 'center', padding: '24px' }}>
          <button
            onClick={handleLoadMore}
            style={{
              backgroundColor: 'transparent',
              border: '1px solid var(--border-medium)',
              borderRadius: '4px',
              padding: '8px 20px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              color: 'var(--text-primary)',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--bg-primary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            Load More
          </button>
        </div>
      )}

      {/* Summary */}
      {!loading && cubes.length > 0 && (
        <div style={{ textAlign: 'center', padding: '8px', color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
          Showing {cubes.length} of {totalElements} cubes
        </div>
      )}

      {/* Fallback slide grid when no cubes (internal only — external members have no shop-slide access) */}
      {!loading && cubes.length === 0 && shopSlides.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))',
          gap: '24px',
          marginTop: '24px',
        }}>
          {shopSlides.map(slide => (
            <SlideCard key={slide.id} slide={slide} showActions={false} />
          ))}
        </div>
      )}

      {/* External (CLIENT/VENDOR) members with no shared cubes yet */}
      {!loading && cubes.length === 0 && !isInternalMember && (
        <div className="empty-state card text-center py-12" style={{ maxWidth: '400px', margin: '24px auto' }}>
          <h3 className="heading-secondary text-xl mb-2">Nothing shared with you yet</h3>
          <p className="text-muted">Collections a shop shares with you will show up here.</p>
        </div>
      )}

      {/* Cube detail modal */}
      {selectedCube && (
        <CubeDetailModal
          cube={selectedCube}
          initialSlides={hydratedSlides[selectedCube.cubeId] || []}
          onClose={() => setSelectedCube(null)}
        />
      )}
    </div>
  );
};

export default HomeTab;
